import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import ResponsiveButton from "../ResponsiveButton/ResponsiveButton";

const STORAGE_KEY = "cookieConsent";

const defaultPreferences = {
  necessary: true,
  preferences: false,
  analytics: true,
  performance: false,
  marketing: false,
  social: false,
};

const CookieConsent = () => {
  const [checked, setChecked] = useState(false);
  const [visible, setVisible] = useState(false);
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [showDetails, setShowDetails] = useState(false);

  /* ===============================
     SCRIPT LOADERS
  =============================== */

  const loadAnalytics = () => {
    if (window.gtag) return;

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-EW62ZV3B16";
    document.head.appendChild(script);

    script.onload = () => {
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag("js", new Date());
      gtag("config", "G-EW62ZV3B16", { anonymize_ip: true });
    };
  };

const applyPreferences = useCallback((prefs) => {
  if (prefs.analytics) {
    loadAnalytics();
  }
}, []);



  /* ===============================
     INIT
  =============================== */
  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    setChecked(true);

    if (!consent) {
      loadAnalytics();
      setVisible(true);
      return;
    }

    try {
      const parsed = JSON.parse(consent);
      if (parsed && typeof parsed === "object") {
        setPreferences(parsed);
        applyPreferences(parsed);
      } else {
        throw new Error("Invalid format");
      }
    } catch (e) {
      localStorage.removeItem(STORAGE_KEY);
      setVisible(true);
    }
  }, [applyPreferences]);

  const savePreferences = (prefs) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    applyPreferences(prefs);
    setVisible(false);
  };

  if (!checked) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed bottom-0 left-0 w-full z-50 bg-linkcol dark:bg-purple-800 p-4 shadow-lg dark:bg-opacity-90"
        >
          <p className="text-center font-customFont1 mb-4 text-sm sm:text-base max-w-4xl mx-auto dark:text-white">
            Käytämme evästeitä parantaaksemme käyttökokemustasi. Voit itse päättää,
            mitä sallitaan.
          </p>

          {/* PIKAVALINNAT */}
          <div className="flex flex-wrap justify-center gap-4 mb-4 ">
            <ResponsiveButton
              variant="addtocart"
              onClick={() =>
                savePreferences({
                  ...defaultPreferences,
                  preferences: true,
                  analytics: true,
                  performance: true,
                  marketing: true,
                  social: true,
                })
              }
            >
              Hyväksy kaikki
            </ResponsiveButton>

            <ResponsiveButton
              variant="removefromcart"
              onClick={() =>
                savePreferences({
                  necessary: true,
                  preferences: false,
                  analytics: false,
                  performance: false,
                  marketing: false,
                  social: false,
                })
              }
            >
              Vain pakolliset
            </ResponsiveButton>

            <ResponsiveButton
              variant="makeabooking"
              onClick={() => setShowDetails((v) => !v)}
            >
              Personoi
            </ResponsiveButton>
          </div>

          {/* YKSITYISKOHDAT */}
          <AnimatePresence>
            {showDetails && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mt-4 flex flex-col gap-4 max-w-md mx-auto font-customFont1"
              >
                <CookieToggle
                  id="necessary"
                  label="Pakolliset evästeet"
                  description="Välttämättömiä sivuston toiminnalle"
                  checked
                  disabled
                />

                <CookieToggle
                  id="preferences"
                  label="Toiminnalliset evästeet"
                  description="Muistavat asetuksesi"
                  checked={preferences.preferences}
                  onChange={(v) =>
                    setPreferences({ ...preferences, preferences: v })
                  }
                />

                <CookieToggle
                  id="analytics"
                  label="Analytiikka"
                  description="Auttaa kehittämään sivustoa (GA4)"
                  checked={preferences.analytics}
                  onChange={(v) =>
                    setPreferences({ ...preferences, analytics: v })
                  }
                />

                <CookieToggle
                  id="performance"
                  label="Suorituskyky"
                  description="Mittarit ja optimointi"
                  checked={preferences.performance}
                  onChange={(v) =>
                    setPreferences({ ...preferences, performance: v })
                  }
                />

                <CookieToggle
                  id="marketing"
                  label="Markkinointi"
                  description="Mainonnan kohdentaminen"
                  checked={preferences.marketing}
                  onChange={(v) =>
                    setPreferences({ ...preferences, marketing: v })
                  }
                />

                <CookieToggle
                  id="social"
                  label="Sosiaalinen media"
                  description="Upotetut some-sisällöt"
                  checked={preferences.social}
                  onChange={(v) =>
                    setPreferences({ ...preferences, social: v })
                  }
                />

                <ResponsiveButton
                  variant="makeabooking"
                  onClick={() => savePreferences(preferences)}
                >
                  Tallenna valinta
                </ResponsiveButton>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ===============================
   COOKIE TOGGLE (A11Y CORRECT)
=============================== */
const CookieToggle = ({
  id,
  label,
  description,
  checked,
  onChange,
  disabled,
}) => {
  const labelId = `${id}-label`;
  const descId = `${id}-desc`;

  return (
    <div
      role="group"
      aria-labelledby={labelId}
      aria-describedby={descId}
      className={`flex items-start gap-3 ${
        disabled ? "opacity-60" : ""
      }`}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-1 w-4 h-4"
      />

      <div>
        <button
          type="button"
          id={labelId}
          disabled={disabled}
          onClick={() => onChange?.(!checked)}
          className="font-semibold text-left dark:text-white disabled:cursor-not-allowed"
        >
          {label}
        </button>

        <div id={descId} className="text-sm dark:text-white opacity-80">
          {description}
        </div>
      </div>
    </div>
  );
};

CookieToggle.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

CookieToggle.defaultProps = {
  onChange: undefined,
  disabled: false,
};

export default CookieConsent;
